import type { Client } from '../types';

export type ClientSortKey =
  | 'name_asc'
  | 'name_desc'
  | 'locations_desc'
  | 'locations_asc'
  | 'location_asc'
  | 'location_desc'
  | 'updated_desc';

export type BusinessTypeFilter = 'all' | 'single' | 'multi';

export interface BusinessSummary {
  head: Client;
  locations: Client[];
  primaryLocation: string;
  updatedAt: number;
  locationTokens: string[];
  categories: string[];
}

export interface ClientListFilters {
  query: string;
  locations: string[];
  categories: string[];
  businessType: BusinessTypeFilter;
}

export const DEFAULT_CLIENT_SORT: ClientSortKey = 'name_asc';

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

export function locationLabel(client: Client): string {
  return client.locationLabel?.trim() || client.location || '';
}

export function buildBusinessSummaries(clients: Client[]): BusinessSummary[] {
  const byId = new Map(clients.map(client => [client.id, client]));
  const businesses = new Map<string, { head: Client; locations: Client[] }>();

  for (const client of clients) {
    const headId = client.parentClientId && byId.has(client.parentClientId)
      ? client.parentClientId
      : client.id;
    if (!businesses.has(headId)) {
      businesses.set(headId, { head: byId.get(headId) ?? client, locations: [] });
    }
    businesses.get(headId)!.locations.push(client);
  }

  return [...businesses.values()].map(({ head, locations }) => {
    const orderedLocations = [...locations].sort((a, b) => {
      if (a.id === head.id) return -1;
      if (b.id === head.id) return 1;
      return compareText(locationLabel(a), locationLabel(b)) || compareText(a.name, b.name) || compareText(a.id, b.id);
    });
    const locationTokens = uniqueSorted(orderedLocations.flatMap(client => splitLocation(client.location)));
    const categories = uniqueSorted(orderedLocations.map(client => client.category).filter(Boolean));

    return {
      head,
      locations: orderedLocations,
      primaryLocation: locationLabel(head),
      updatedAt: Math.max(...orderedLocations.map(client => client.updatedAt || 0)),
      locationTokens,
      categories,
    };
  });
}

export function getClientFilterOptions(businesses: BusinessSummary[]): { locations: string[]; categories: string[] } {
  return {
    locations: uniqueSorted(businesses.flatMap(business => business.locationTokens)),
    categories: uniqueSorted(businesses.flatMap(business => business.categories)),
  };
}

export function filterBusinesses(businesses: BusinessSummary[], filters: ClientListFilters): BusinessSummary[] {
  const query = normalise(filters.query);
  const selectedLocations = new Set(filters.locations.map(normalise));
  const selectedCategories = new Set(filters.categories.map(normalise));

  return businesses.filter(business => {
    if (filters.businessType === 'single' && business.locations.length !== 1) return false;
    if (filters.businessType === 'multi' && business.locations.length < 2) return false;

    if (selectedLocations.size > 0 && !business.locationTokens.some(token => selectedLocations.has(normalise(token)))) return false;
    if (selectedCategories.size > 0 && !business.categories.some(category => selectedCategories.has(normalise(category)))) return false;

    if (!query) return true;
    return business.locations.some(client => [client.name, client.location, client.locationLabel, client.category]
      .some(value => normalise(value).includes(query)));
  });
}

export function sortBusinesses(businesses: BusinessSummary[], sort: ClientSortKey): BusinessSummary[] {
  const direction = sort.endsWith('_desc') ? -1 : 1;
  return [...businesses].sort((a, b) => {
    let result = 0;
    switch (sort) {
      case 'name_asc':
      case 'name_desc':
        result = compareText(a.head.name, b.head.name);
        break;
      case 'locations_asc':
      case 'locations_desc':
        result = a.locations.length - b.locations.length;
        break;
      case 'location_asc':
      case 'location_desc':
        result = compareText(a.primaryLocation, b.primaryLocation);
        break;
      case 'updated_desc':
        result = a.updatedAt - b.updatedAt;
        break;
    }
    if (result !== 0) return result * direction;
    return compareText(a.head.name, b.head.name) || compareText(a.head.id, b.head.id);
  });
}

export function isClientSortKey(value: string | null): value is ClientSortKey {
  return value === 'name_asc' || value === 'name_desc' || value === 'locations_desc'
    || value === 'locations_asc' || value === 'location_asc' || value === 'location_desc'
    || value === 'updated_desc';
}

function splitLocation(location: string | undefined): string[] {
  return (location ?? '').split(',').map(value => value.trim()).filter(Boolean);
}

function uniqueSorted(values: string[]): string[] {
  const known = new Set<string>();
  return values.filter(value => {
    const key = normalise(value);
    if (!key || known.has(key)) return false;
    known.add(key);
    return true;
  }).sort(compareText);
}

function compareText(a: string, b: string): number {
  return collator.compare(a || '', b || '');
}

function normalise(value: string | undefined | null): string {
  return (value ?? '').trim().toLocaleLowerCase();
}
